import { Component, inject, OnInit } from '@angular/core';
import { CommonModule, DecimalPipe, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SuppliersService } from '../../core/services/suppliers.service';
import { PayablesService } from '../../core/services/payables.service';
import { PaymentsService } from '../../core/services/payments.service';
import { Supplier } from '../../core/models/supplier.model';
import { PayableDocument, PaymentStatus } from '../../core/models/payable.model';
import { CreatePaymentDto, ManualAllocation } from '../../core/models/payment.model';

interface DocumentSelection {
  document: PayableDocument;
  selected: boolean;
  amount: number;
  payFull: boolean;
}

@Component({
  selector: 'app-create-payment-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatIconModule,
    MatCheckboxModule,
    MatSlideToggleModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    DecimalPipe,
    DatePipe
  ],
  template: `
    <h2 mat-dialog-title>Nova uplata</h2>
    <mat-dialog-content class="dialog-content">
      <div class="form-row">
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Dobavljač</mat-label>
          <mat-select [(ngModel)]="form.supplierId" (selectionChange)="onSupplierChange()" required>
            <mat-option *ngFor="let s of suppliers" [value]="s.id">{{ s.name }}</mat-option>
          </mat-select>
        </mat-form-field>
      </div>

      <div class="form-row two-cols">
        <mat-form-field appearance="outline">
          <mat-label>Iznos</mat-label>
          <input matInput type="number" [(ngModel)]="form.amount" required min="0.01" step="0.01">
          <span matSuffix>RSD</span>
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>Datum uplate</mat-label>
          <input matInput [matDatepicker]="picker" [(ngModel)]="paymentDate" required>
          <mat-datepicker-toggle matSuffix [for]="picker"></mat-datepicker-toggle>
          <mat-datepicker #picker></mat-datepicker>
        </mat-form-field>
      </div>

      <div class="form-row two-cols">
        <mat-form-field appearance="outline">
          <mat-label>Poziv na broj</mat-label>
          <input matInput [(ngModel)]="form.referenceNumber">
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>Valuta</mat-label>
          <mat-select [(ngModel)]="form.paymentCurrency">
            <mat-option value="RSD">RSD</mat-option>
            <mat-option value="EUR">EUR</mat-option>
            <mat-option value="USD">USD</mat-option>
          </mat-select>
        </mat-form-field>
      </div>

      <mat-form-field appearance="outline" class="full-width">
        <mat-label>Napomena</mat-label>
        <textarea matInput [(ngModel)]="form.note" rows="2"></textarea>
      </mat-form-field>

      <div class="allocation-toggle">
        <mat-slide-toggle [(ngModel)]="autoAllocate" (change)="onAllocationModeChange()">
          Automatsko raspoređivanje (FIFO)
        </mat-slide-toggle>
        <span class="toggle-hint" *ngIf="autoAllocate">Uplata će biti automatski raspoređena na najstarija neplaćena dugovanja</span>
        <span class="toggle-hint" *ngIf="!autoAllocate">Ručno izaberite dugovanja za raspoređivanje</span>
      </div>

      <!-- Manual allocation section -->
      <div class="manual-allocation-section" *ngIf="!autoAllocate && form.supplierId">
        <div class="section-header">
          <h3>Neplaćena dugovanja</h3>
          <span class="doc-count" *ngIf="!loadingDocuments">{{ unpaidDocuments.length }} dokumenata</span>
        </div>

        <div class="loading-docs" *ngIf="loadingDocuments">
          <mat-spinner diameter="24"></mat-spinner>
          <span>Učitavanje dugovanja...</span>
        </div>

        <div class="no-documents" *ngIf="!loadingDocuments && unpaidDocuments.length === 0">
          <mat-icon>check_circle</mat-icon>
          <span>Nema neplaćenih dugovanja za ovog dobavljača</span>
        </div>

        <div class="documents-list" *ngIf="!loadingDocuments && unpaidDocuments.length > 0">
          <div class="document-item" *ngFor="let item of documentSelections" [class.selected]="item.selected">
            <div class="doc-checkbox">
              <mat-checkbox [(ngModel)]="item.selected" (change)="onDocumentSelectionChange(item)"></mat-checkbox>
            </div>
            <div class="doc-info">
              <div class="doc-header">
                <span class="doc-number">{{ item.document.invoiceNumber || 'Bez broja' }}</span>
                <span class="doc-date" *ngIf="item.document.invoiceDate">{{ item.document.invoiceDate | date:'dd.MM.yyyy' }}</span>
              </div>
              <div class="doc-amounts">
                <span class="doc-total">Ukupno: {{ item.document.amount | number:'1.2-2' }}</span>
                <span class="doc-remaining">Preostalo: {{ item.document.remainingAmount | number:'1.2-2' }}</span>
              </div>
            </div>
            <div class="doc-allocation" *ngIf="item.selected">
              <mat-checkbox [(ngModel)]="item.payFull" (change)="onPayFullChange(item)" class="pay-full-checkbox">
                Ceo iznos
              </mat-checkbox>
              <mat-form-field appearance="outline" class="amount-field" *ngIf="!item.payFull">
                <mat-label>Iznos</mat-label>
                <input matInput type="number" [(ngModel)]="item.amount"
                       [max]="item.document.remainingAmount" min="0.01" step="0.01"
                       (ngModelChange)="calculateTotalAllocated()">
              </mat-form-field>
            </div>
          </div>
        </div>

        <div class="allocation-summary" *ngIf="hasSelectedDocuments()">
          <div class="summary-row">
            <span>Iznos uplate:</span>
            <span class="amount">{{ form.amount | number:'1.2-2' }} RSD</span>
          </div>
          <div class="summary-row">
            <span>Raspoređeno:</span>
            <span class="amount" [class.over]="totalAllocated > form.amount">{{ totalAllocated | number:'1.2-2' }} RSD</span>
          </div>
          <div class="summary-row" *ngIf="form.amount - totalAllocated > 0">
            <span>Neraspoređeno:</span>
            <span class="amount unallocated">{{ form.amount - totalAllocated | number:'1.2-2' }} RSD</span>
          </div>
          <div class="allocation-warning" *ngIf="totalAllocated > form.amount">
            <mat-icon>warning</mat-icon>
            Raspoređeni iznos premašuje iznos uplate
          </div>
        </div>
      </div>

      <div class="select-supplier-hint" *ngIf="!autoAllocate && !form.supplierId">
        <mat-icon>info</mat-icon>
        <span>Izaberite dobavljača da biste videli neplaćena dugovanja</span>
      </div>
    </mat-dialog-content>

    <mat-dialog-actions align="end">
      <button mat-button mat-dialog-close>Otkaži</button>
      <button mat-raised-button color="primary"
              [disabled]="!isValid() || saving"
              (click)="save()">
        <mat-spinner diameter="20" *ngIf="saving"></mat-spinner>
        <span *ngIf="!saving">Sačuvaj</span>
      </button>
    </mat-dialog-actions>
  `,
  styles: [`
    .dialog-content {
      min-width: 500px;
      max-width: 600px;
    }

    .form-row {
      margin-bottom: 8px;
    }

    .form-row.two-cols {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 16px;
    }

    .full-width {
      width: 100%;
    }

    mat-form-field {
      width: 100%;
    }

    .allocation-toggle {
      margin: 16px 0;
      padding: 16px;
      background: #F9FAFB;
      border-radius: 8px;
      border: 1px solid #E5E7EB;
    }

    .toggle-hint {
      display: block;
      margin-top: 8px;
      font-size: 12px;
      color: #6B7280;
    }

    .manual-allocation-section {
      margin-top: 16px;
      padding: 16px;
      background: #F9FAFB;
      border-radius: 8px;
      border: 1px solid #E5E7EB;
    }

    .section-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 12px;
    }

    .section-header h3 {
      margin: 0;
      font-size: 14px;
      font-weight: 600;
      color: #374151;
    }

    .doc-count {
      font-size: 12px;
      color: #6B7280;
    }

    .loading-docs {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 20px;
      justify-content: center;
      color: #6B7280;
    }

    .no-documents {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 20px;
      justify-content: center;
      color: #10B981;
    }

    .no-documents mat-icon {
      color: #10B981;
    }

    .documents-list {
      max-height: 300px;
      overflow-y: auto;
    }

    .document-item {
      display: flex;
      align-items: flex-start;
      gap: 12px;
      padding: 12px;
      background: white;
      border: 1px solid #E5E7EB;
      border-radius: 6px;
      margin-bottom: 8px;
      transition: all 0.2s ease;
    }

    .document-item.selected {
      border-color: #3B82F6;
      background: #EFF6FF;
    }

    .doc-checkbox {
      padding-top: 2px;
    }

    .doc-info {
      flex: 1;
      min-width: 0;
    }

    .doc-header {
      display: flex;
      justify-content: space-between;
      margin-bottom: 4px;
    }

    .doc-number {
      font-weight: 600;
      color: #1F2937;
    }

    .doc-date {
      font-size: 12px;
      color: #6B7280;
    }

    .doc-amounts {
      display: flex;
      gap: 16px;
      font-size: 12px;
    }

    .doc-total {
      color: #6B7280;
    }

    .doc-remaining {
      color: #DC2626;
      font-weight: 500;
    }

    .doc-allocation {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      gap: 8px;
    }

    .pay-full-checkbox {
      font-size: 12px;
    }

    .amount-field {
      width: 120px;
    }

    .amount-field ::ng-deep .mat-mdc-form-field-subscript-wrapper {
      display: none;
    }

    .allocation-summary {
      margin-top: 16px;
      padding: 12px;
      background: white;
      border-radius: 6px;
      border: 1px solid #E5E7EB;
    }

    .summary-row {
      display: flex;
      justify-content: space-between;
      padding: 4px 0;
      font-size: 13px;
    }

    .summary-row .amount {
      font-weight: 600;
      font-family: 'Roboto Mono', monospace;
    }

    .summary-row .amount.over {
      color: #DC2626;
    }

    .summary-row .amount.unallocated {
      color: #F59E0B;
    }

    .allocation-warning {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-top: 8px;
      padding: 8px;
      background: #FEF2F2;
      border-radius: 4px;
      color: #DC2626;
      font-size: 12px;
    }

    .allocation-warning mat-icon {
      font-size: 18px;
      width: 18px;
      height: 18px;
    }

    .select-supplier-hint {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 20px;
      background: #F9FAFB;
      border-radius: 8px;
      border: 1px solid #E5E7EB;
      color: #6B7280;
      justify-content: center;
    }

    mat-dialog-actions button mat-spinner {
      display: inline-block;
    }
  `]
})
export class CreatePaymentDialogComponent implements OnInit {
  private dialogRef = inject(MatDialogRef<CreatePaymentDialogComponent>);
  private suppliersService = inject(SuppliersService);
  private payablesService = inject(PayablesService);
  private paymentsService = inject(PaymentsService);

  suppliers: Supplier[] = [];
  unpaidDocuments: PayableDocument[] = [];
  documentSelections: DocumentSelection[] = [];

  autoAllocate = true;
  loadingDocuments = false;
  saving = false;
  totalAllocated = 0;

  paymentDate: Date = new Date();

  form: CreatePaymentDto = {
    supplierId: '',
    amount: 0,
    paymentDate: new Date().toISOString(),
    referenceNumber: '',
    note: '',
    paymentCurrency: 'RSD',
    autoAllocate: true,
    manualAllocations: []
  };

  ngOnInit(): void {
    this.suppliersService.getAll().subscribe({
      next: (list) => this.suppliers = list
    });
  }

  onSupplierChange(): void {
    if (!this.autoAllocate && this.form.supplierId) {
      this.loadUnpaidDocuments();
    }
  }

  onAllocationModeChange(): void {
    this.form.autoAllocate = this.autoAllocate;
    if (!this.autoAllocate && this.form.supplierId) {
      this.loadUnpaidDocuments();
    }
  }

  loadUnpaidDocuments(): void {
    this.loadingDocuments = true;
    this.payablesService.getFiltered({
      supplierId: this.form.supplierId,
      status: PaymentStatus.Unpaid,
      pageSize: 100
    }).subscribe({
      next: (result) => {
        // Filter to only unpaid or partially paid
        this.unpaidDocuments = result.items.filter(d => d.remainingAmount > 0);
        this.documentSelections = this.unpaidDocuments.map(doc => ({
          document: doc,
          selected: false,
          amount: doc.remainingAmount,
          payFull: true
        }));
        this.loadingDocuments = false;
      },
      error: () => {
        this.loadingDocuments = false;
      }
    });

    // Also load partially paid
    this.payablesService.getFiltered({
      supplierId: this.form.supplierId,
      status: PaymentStatus.PartiallyPaid,
      pageSize: 100
    }).subscribe({
      next: (result) => {
        const partiallyPaid = result.items.filter(d => d.remainingAmount > 0);
        partiallyPaid.forEach(doc => {
          if (!this.unpaidDocuments.find(d => d.id === doc.id)) {
            this.unpaidDocuments.push(doc);
            this.documentSelections.push({
              document: doc,
              selected: false,
              amount: doc.remainingAmount,
              payFull: true
            });
          }
        });
      }
    });
  }

  onDocumentSelectionChange(item: DocumentSelection): void {
    if (item.selected && item.payFull) {
      item.amount = item.document.remainingAmount;
    }
    this.calculateTotalAllocated();
  }

  onPayFullChange(item: DocumentSelection): void {
    if (item.payFull) {
      item.amount = item.document.remainingAmount;
    }
    this.calculateTotalAllocated();
  }

  calculateTotalAllocated(): void {
    this.totalAllocated = this.documentSelections
      .filter(d => d.selected)
      .reduce((sum, d) => sum + (d.amount || 0), 0);
  }

  hasSelectedDocuments(): boolean {
    return this.documentSelections.some(d => d.selected);
  }

  isValid(): boolean {
    if (!this.form.supplierId || !this.form.amount || this.form.amount <= 0 || !this.paymentDate) {
      return false;
    }

    if (!this.autoAllocate) {
      // For manual allocation, check that allocated amount doesn't exceed payment
      if (this.totalAllocated > this.form.amount) {
        return false;
      }
    }

    return true;
  }

  save(): void {
    if (!this.isValid()) return;

    this.saving = true;
    this.form.paymentDate = this.paymentDate.toISOString();
    this.form.autoAllocate = this.autoAllocate;

    if (!this.autoAllocate) {
      this.form.manualAllocations = this.documentSelections
        .filter(d => d.selected && d.amount > 0)
        .map(d => ({
          payableDocumentId: d.document.id,
          amount: d.amount
        }));
    } else {
      this.form.manualAllocations = [];
    }

    this.paymentsService.create(this.form).subscribe({
      next: (payment) => {
        this.saving = false;
        this.dialogRef.close(payment);
      },
      error: (err) => {
        this.saving = false;
        alert(err?.error?.message || 'Greška pri čuvanju uplate');
      }
    });
  }
}
